// ---------------------------------------------------------------------------
// Adapters — convert between app-specific types and promptlayer-ui types
//
// The frontend (promptlayer_app) uses its own `Content` / `ChatMessageI`
// types which have a slightly different shape than the shared
// `ContentBlock` / `DisplayMessage` types in promptlayer-ui.
//
// These adapters live in the shared package so both Vibes and the frontend
// can use them. They are intentionally lenient — unknown content types are
// passed through with a type assertion so new block types don't silently
// disappear.
// ---------------------------------------------------------------------------

import type {
  ContentBlock,
  DisplayMessage,
  ErrorInfo,
  ToolCall,
  ChatMessageMetadata,
} from "./types";

// ---------------------------------------------------------------------------
// Content → ContentBlock
// ---------------------------------------------------------------------------

/**
 * A loose "content item" shape — intentionally permissive so it can accept
 * any object with a `type` string and arbitrary payload. Both the frontend's
 * `Content` type and the Vibes `ContentBlock` are assignable to this.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyContentItem = { type: string } & Record<string, any>;

/**
 * Convert an array of app-specific content items into shared `ContentBlock[]`.
 *
 * The mapping is mostly identity (the field shapes are compatible) but this
 * function serves as the canonical boundary where we can add transforms
 * if the upstream shape ever diverges.
 *
 * Unknown content types are passed through as-is (cast to ContentBlock) so
 * the rendering layer can still attempt to display them or silently skip
 * them in the bucketing step.
 */
export function adaptContent(content: AnyContentItem[]): ContentBlock[] {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.map(adaptSingleBlock);
}

function adaptSingleBlock(item: AnyContentItem): ContentBlock {
  // The frontend and shared types are structurally compatible for every
  // known content type. We simply cast here — the ContentBlocks renderer
  // performs its own type-narrowing via the `type` discriminant.
  return item as unknown as ContentBlock;
}

// ---------------------------------------------------------------------------
// Playground content block wrapper → ContentBlock
// ---------------------------------------------------------------------------

/**
 * Shape of the wrapper objects returned by the Playground's
 * `MessageStore.contentBlocks` getter.  Each entry wraps the raw content
 * item with its original index in the store's `_content` array.
 *
 * ```ts
 * { type: "text", content: { type: "text", text: "hello" }, index: 0 }
 * ```
 */
export interface PlaygroundBlockWrapper {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: { type: string } & Record<string, any>;
  /** Original index inside the MobX `_content` array. */
  index: number;
}

/**
 * Convert the Playground's `{ type, content, index }` wrappers into flat
 * `ContentBlock[]` suitable for `<ContentBlocks />`.
 *
 * The `content` payload is structurally compatible with `ContentBlock` — the
 * wrapper just adds an extra `index` that the shared renderer doesn't need.
 *
 * Usage in the Playground:
 * ```tsx
 * import { adaptPlaygroundBlocks } from "@promptlayer/ui";
 *
 * <ContentBlocks
 *   content={adaptPlaygroundBlocks(message.contentBlocks)}
 *   blockOverrides={overrides}
 * />
 * ```
 */
export function adaptPlaygroundBlocks(
  blocks: PlaygroundBlockWrapper[],
): ContentBlock[] {
  if (!Array.isArray(blocks)) {
    return [];
  }
  return blocks.map((wrapper) => adaptSingleBlock(wrapper.content));
}

// ---------------------------------------------------------------------------
// ChatMessageI → DisplayMessage
// ---------------------------------------------------------------------------

/**
 * Loose shape for a "chat message" from any consuming app.
 * This is intentionally a structural type rather than importing `ChatMessageI`
 * directly so the shared package has no dependency on the frontend.
 */
export interface AdaptableMessage {
  id: string;
  role: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any[] | string;
  tool_calls?: ToolCall[];
  name?: string;
  tool_call_id?: string;
  // Frontend-specific fields
  individual_run_request_id?: number;
  request_id?: number;
  cost?: number;
  latency_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  created_at?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: { type?: string; message: string; verbose?: string } | any;
  // Streaming flag (may be set by the consumer)
  isStreaming?: boolean;
}

/**
 * Convert an app-specific message into a shared `DisplayMessage`.
 *
 * Usage in the frontend:
 * ```ts
 * import { adaptMessage } from "promptlayer-ui";
 * const displayMsg = adaptMessage(chatMessageI);
 * ```
 */
export function adaptMessage(msg: AdaptableMessage): DisplayMessage {
  // Normalise content — the frontend always has Content[] but there could
  // be edge cases where it's a string.
  // eslint-disable-next-line no-nested-ternary
  const rawContent = Array.isArray(msg.content)
    ? msg.content
    : typeof msg.content === "string"
      ? [{ type: "text" as const, text: msg.content }]
      : [];

  const content = adaptContent(rawContent as AnyContentItem[]);

  // Normalise role
  const role = normalizeRole(msg.role);

  // Build metadata from frontend stats fields
  const metadata = buildMetadata(msg);

  // Build error info
  const error = buildError(msg.error);

  return {
    id: msg.id,
    role,
    content,
    tool_calls: msg.tool_calls,
    name: msg.name,
    tool_call_id: msg.tool_call_id,
    isStreaming: msg.isStreaming,
    metadata: metadata ?? undefined,
    error: error ?? undefined,
  };
}

/**
 * Batch-convert an array of messages.
 */
export function adaptMessages(msgs: AdaptableMessage[]): DisplayMessage[] {
  return msgs.map(adaptMessage);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeRole(role: string): "user" | "assistant" | "tool" {
  switch (role) {
    case "user":
      return "user";
    case "tool":
    case "function":
      return "tool";
    case "assistant":
    case "system":
    case "developer":
    default:
      return "assistant";
  }
}

function buildMetadata(msg: AdaptableMessage): ChatMessageMetadata | null {
  const hasStats =
    msg.individual_run_request_id !== null ||
    msg.cost !== null ||
    msg.latency_ms !== null;

  if (!hasStats) {
    return null;
  }

  return {
    individual_run_request_id: msg.individual_run_request_id ?? 0,
    price: msg.cost ?? null,
    latency_ms: msg.latency_ms ?? null,
    tokens:
      (msg.input_tokens !== null && msg.input_tokens !== undefined) ||
      (msg.output_tokens !== null && msg.output_tokens !== undefined)
        ? {
            prompt_tokens: msg.input_tokens ?? null,
            completion_tokens: msg.output_tokens ?? null,
            total_tokens:
              msg.input_tokens !== null &&
              msg.input_tokens !== undefined &&
              msg.output_tokens !== null &&
              msg.output_tokens !== undefined
                ? msg.input_tokens + msg.output_tokens
                : null,
          }
        : null,
    model: null,
    created_at: msg.created_at ?? null,
  };
}

function buildError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
): ErrorInfo | null {
  if (!error) {
    return null;
  }

  if (typeof error === "string") {
    return { type: "error", message: error };
  }

  if (typeof error === "object" && error !== null) {
    return {
      type: "error",
      message: error.message ?? "Unknown error",
      verbose: error.verbose ?? undefined,
    };
  }

  return null;
}
